import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import {
  acceptTeamInvite,
  ApiError,
  getAuthMode,
  login as apiLogin,
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { applyExplicitLogoutBoundary, unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import {
  authSearchParam,
  buildAuth0LoginPath,
  isLegacyRequested,
} from "@/features/auth/authPaths";

/**
 * M9b E1–E7 — `/invite/{token}` invite landing.
 *
 * The token comes from the URL path. Two render branches:
 *   - Signed-in user: a one-button Accept that POSTs the token to
 *     `/api/team/invites/accept`. On success, route by
 *     `membership.role`.
 *   - Unauthenticated user: Auth0 entry when enabled, returning to
 *     this same invite URL after authentication. In legacy/dual
 *     mode, the migration fallback form can still create a local
 *     account and auto-login via `/api/login`.
 *
 * Post-accept routing by role (matches legacy `routeAfterInviteAcceptance`):
 *   - guardian / player → /feedback
 *   - team_admin       → /admin/people
 *   - coach / assistant_coach → /coach
 *   - anything else    → /  (defensive)
 *
 * Error states (E5) — backend returns three distinct statuses for
 * the unhappy paths and the UI gives the operator a useful
 * distinction:
 *   - 404 → "That invite is invalid or expired."
 *   - 409 → "This invite has already been used."
 *   - 410 → "This invite has expired."
 *
 * Privacy invariant (E6): the URL token only moves from the path to
 * the Auth0 `next` path or the accept mutation body. Never logged.
 * The legacy create-account form clears `password` on submit so the
 * value doesn't linger in React state after a failed attempt.
 */
export function InviteAcceptPage() {
  const params = useParams({ from: "/invite/$token" });
  const search = useSearch({ from: "/invite/$token" });
  const token = decodeURIComponent(params.token);
  const me = useMe();
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const auth0Enabled = authMode.data?.auth0_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));

  if (me.isLoading) {
    return (
      <main
        data-testid="invite-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }

  return (
    <main
      data-testid="invite-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Accept team invite"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-4"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            You&rsquo;re invited
          </h1>
          <p className="text-ink-muted text-xs">
            Create or use your account to join this team.
          </p>
        </header>

        {me.data?.user ? (
          <SignedInBranch
            token={token}
            username={me.data.user.username}
            auth0Enabled={auth0Enabled}
            authModeReady={!authMode.isPending}
          />
        ) : (
          <UnauthenticatedBranch
            token={token}
            auth0Enabled={auth0Enabled}
            legacyEnabled={legacyEnabled}
            legacyRequested={legacyRequested}
            loadingMode={authMode.isPending}
          />
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// E2 — Signed-in branch
// ---------------------------------------------------------------------------

function SignedInBranch({
  token,
  username,
  auth0Enabled,
  authModeReady,
}: {
  token: string;
  username: string;
  auth0Enabled: boolean;
  authModeReady: boolean;
}) {
  const navigate = useNavigate({ from: "/invite/$token" });
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const accept = useMutation({
    mutationFn: () => acceptTeamInvite({ token }),
    onSuccess: async (resp) => {
      setSuccess(true);
      // The user's memberships changed; refresh /api/me before
      // routing so the destination shell sees the new scope.
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      window.setTimeout(() => {
        void navigate({
          to: routeForRole(resp.membership.role),
          replace: true,
        });
      }, 600);
    },
    onError: (err) => {
      setError(formatInviteError(err));
    },
  });

  const switchAccount = async () => {
    const next = invitePathForToken(token);
    if (auth0Enabled) {
      window.location.assign(
        `/auth/logout?returnTo=${encodeURIComponent(next)}`,
      );
      return;
    }
    try {
      await apiLogout();
    } catch {
      // ignore — server might already have revoked
    }
    applyExplicitLogoutBoundary();
    // Keep the invite flow anchored on the same URL after switching
    // accounts: the next signed-in user should come back here after
    // authenticating, not fall through to their default landing.
    void navigate({
      to: "/login",
      search: { next },
      replace: true,
    });
  };

  return (
    <div data-testid="invite-signed-in" className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">
        You&rsquo;re signed in as{" "}
        <span className="font-mono text-ink">@{username}</span>. Accepting
        will link this team to your account.
      </p>

      {success ? (
        <p
          data-testid="invite-accept-success"
          className="text-good text-xs font-mono"
        >
          Invite accepted. Taking you to your team…
        </p>
      ) : null}

      {error ? (
        <p
          data-testid="invite-accept-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => {
            setError(null);
            accept.mutate();
          }}
          disabled={accept.isPending || success}
          data-testid="invite-accept-btn"
        >
          {accept.isPending ? "Accepting…" : "Accept invite"}
        </Button>
        <button
          type="button"
          onClick={() => void switchAccount()}
          disabled={accept.isPending || success || !authModeReady}
          data-testid="invite-switch-account"
          className="rounded border border-rule px-3 py-2 text-sm font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-field transition-colors disabled:opacity-50"
        >
          Sign in as someone else
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// E3 — Unauthenticated create-account branch
// ---------------------------------------------------------------------------

function UnauthenticatedBranch({
  token,
  auth0Enabled,
  legacyEnabled,
  legacyRequested,
  loadingMode,
}: {
  token: string;
  auth0Enabled: boolean;
  legacyEnabled: boolean;
  legacyRequested: boolean;
  loadingMode: boolean;
}) {
  const showLegacyCreate = legacyEnabled && (!auth0Enabled || legacyRequested);
  const shouldStartAuth0 = auth0Enabled && !legacyRequested && !loadingMode;
  const next = invitePathForToken(token);
  const auth0LoginPath = buildAuth0LoginPath(next, { screenHint: "signup" });
  const auth0DelayMs = legacyEnabled ? 700 : 0;

  useEffect(() => {
    if (!shouldStartAuth0) return;
    const redirect = () => {
      window.location.replace(auth0LoginPath);
    };
    if (auth0DelayMs === 0) {
      redirect();
      return;
    }
    const timer = window.setTimeout(redirect, auth0DelayMs);
    return () => window.clearTimeout(timer);
  }, [auth0DelayMs, auth0LoginPath, shouldStartAuth0]);

  return (
    <div className="flex flex-col gap-4">
      {loadingMode ? (
        <p
          data-testid="invite-auth-mode-loading"
          className="font-mono text-xs text-ink-muted"
        >
          Checking account options...
        </p>
      ) : null}
      {shouldStartAuth0 ? (
        <Auth0InviteBranch
          next={next}
          legacyEnabled={legacyEnabled}
        />
      ) : null}
      {showLegacyCreate ? (
        <CreateAccountBranch token={token} auth0Enabled={auth0Enabled} />
      ) : null}
    </div>
  );
}

function Auth0InviteBranch({
  next,
  legacyEnabled,
}: {
  next: `/invite/${string}`;
  legacyEnabled: boolean;
}) {
  return (
    <section
      data-testid="invite-auth0-handoff"
      className="rounded border border-rule bg-canvas p-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
          <ShieldCheck aria-hidden="true" className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-base font-semibold text-ink">
            Opening account portal
          </h2>
          <p className="mt-1 text-xs leading-5 text-ink-muted">
            Auth0 will handle sign-in or account creation, then Tktic will
            bring you back to this invite.
          </p>
        </div>
      </div>
      <p
        data-testid="invite-auth0-progress"
        className="mt-4 inline-flex items-center gap-2 font-mono text-xs text-ink-muted"
      >
        <LoaderCircle aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        Redirecting...
      </p>
      {legacyEnabled ? (
        <a
          href={`${next}?legacy=1`}
          data-testid="invite-legacy-link"
          className="mt-3 inline-block rounded px-1 py-0.5 text-left font-mono text-[0.65rem] text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Use legacy account form
        </a>
      ) : null}
    </section>
  );
}

function CreateAccountBranch({
  token,
  auth0Enabled,
}: {
  token: string;
  auth0Enabled: boolean;
}) {
  const navigate = useNavigate({ from: "/invite/$token" });
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const next = invitePathForToken(token);
  const signInPath = auth0Enabled
    ? buildAuth0LoginPath(next)
    : `/login?next=${encodeURIComponent(next)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);
    const u = username.trim();
    if (!u) {
      setError("Username is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    // Capture the password locally then clear React state before
    // the network calls — the on-screen field clears immediately
    // and the in-memory value lives only for the duration of the
    // two API calls below (E6 privacy invariant).
    const pw = password;
    setPassword("");
    try {
      const acceptResp = await acceptTeamInvite({
        token,
        username: u,
        password: pw,
        display_name: displayName.trim() || undefined,
      });
      setSuccess("Account created. Signing you in…");
      // Auto-login with the new credentials so the user lands
      // authenticated. If this fails for any reason, fall back to
      // the manual sign-in path (the account exists, the operator
      // just needs to authenticate explicitly).
      try {
        const loginResp = await apiLogin({ username: u, password: pw });
        setAuthToken(loginResp.token);
        await queryClient.invalidateQueries();
        window.setTimeout(() => {
          void navigate({
            to: routeForRole(acceptResp.membership.role),
            replace: true,
          });
        }, 600);
      } catch {
        setError(
          "Account created, but automatic sign-in failed. Sign in with your new account to continue.",
        );
        window.setTimeout(() => {
          void navigate({ to: "/login", replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(formatInviteError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="invite-create-account"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-3"
    >
      <p className="text-sm text-ink-muted">
        Create an account and join the team with this invite. Already have one?{" "}
        <a
          href={signInPath}
          data-testid="invite-sign-in-instead"
          className="text-accent hover:underline"
        >
          Sign in instead
        </a>
        .
      </p>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Username
        </span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          minLength={1}
          maxLength={100}
          autoComplete="username"
          data-testid="invite-create-username"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Display name
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
          autoComplete="name"
          data-testid="invite-create-display-name"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          Optional. Visible to your teammates.
        </span>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Choose a password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          maxLength={200}
          autoComplete="new-password"
          data-testid="invite-create-password"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          At least 8 characters.
        </span>
      </label>

      {error ? (
        <p
          data-testid="invite-create-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="invite-create-success"
          className="text-good text-xs font-mono"
        >
          {success}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="invite-create-submit"
        className="self-start"
      >
        {submitting ? "Creating…" : "Create account and join team"}
      </Button>
    </form>
  );
}

function invitePathForToken(token: string): `/invite/${string}` {
  return `/invite/${encodeURIComponent(token)}`;
}

// ---------------------------------------------------------------------------
// E4 — role → destination
// ---------------------------------------------------------------------------

function routeForRole(
  role: string,
): "/feedback" | "/admin/people" | "/coach" | "/" {
  const normalized = (role || "").toLowerCase();
  if (normalized === "guardian" || normalized === "player") return "/feedback";
  if (normalized === "team_admin") return "/admin/people";
  if (normalized === "coach" || normalized === "assistant_coach")
    return "/coach";
  return "/";
}

// ---------------------------------------------------------------------------
// E5 — error mapping
// ---------------------------------------------------------------------------

function formatInviteError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return "That invite is invalid or expired.";
    if (err.status === 409) return "This invite has already been used.";
    if (err.status === 410) return "This invite has expired.";
    return typeof err.detail === "string"
      ? err.detail
      : `Could not accept the invite (HTTP ${err.status}).`;
  }
  return "Network error. Try again in a moment.";
}
